import { Alert, Textarea } from "@xola/ui-kit";
import { get, map } from "lodash";
import React, { ChangeEvent, useEffect, useState } from "react";
import { CouponCode, CouponCodeDuplicateOption } from "types";
import { getUniqueCodes } from "@/models/Coupon";

interface CouponCodeInputProps {
    value: CouponCode[];
    onChange: (values: CouponCode[]) => void;
    onFocus?: () => void;
    uses?: number;
    isError?: boolean;
    error: any;
    duplicateOption: CouponCodeDuplicateOption | null;
    onDuplicateOptionChange: (duplicateOption: CouponCodeDuplicateOption) => void;
}

const INVALID_CHARACTERS_ERROR = "Code cannot contain spaces, quotes, <, >, &";

export const CouponCodeInput = ({
    value,
    onChange,
    onFocus,
    uses = 0,
    isError,
    error,
    duplicateOption,
    onDuplicateOptionChange,
}: CouponCodeInputProps) => {
    const [stringValue, setStringValue] = useState("");

    useEffect(() => {
        setStringValue(value.map((couponCode) => couponCode.code).join("\n"));
    }, [value, setStringValue]);

    const handleCodeInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setStringValue(e.target.value);
    };

    const handleCodeInputBlur = () => {
        const uniqueCodes = getUniqueCodes(stringValue);
        const uniqueCodesString = uniqueCodes.join("\n");
        setStringValue(uniqueCodesString);
        onChange(uniqueCodesString ? uniqueCodes.map((code) => ({ code, uses })) : []);
    };

    const codeAlreadyExistsError =
        get(error, "response.data.field.codes.error") === "code_already_exists"
            ? (error?.response?.data as CodeAlreadyExistsError)
            : null;

    const codeErrors = get(error, "response.data.field.codes");

    const codeContainsInvalidCharacters = codeErrors && Object.values(codeErrors).includes(INVALID_CHARACTERS_ERROR);

    return (
        <>
            <Textarea
                isRequired
                isError={isError}
                className="h-25 font-mono leading-5"
                value={stringValue}
                onChange={handleCodeInputChange}
                onBlur={handleCodeInputBlur}
                onFocus={onFocus}
            />
            {codeContainsInvalidCharacters && <span className="text-sm text-danger">{INVALID_CHARACTERS_ERROR}</span>}

            {codeAlreadyExistsError ? (
                <div className="mt-4">
                    <Alert color="danger">
                        <div className="space-y-2">
                            <div className="font-bold">Upload failed</div>

                            <div>
                                Sorry, we couldn&apos;t complete the upload because one or more of the codes already
                                exist in your other coupon campaigns or gifts or store credits. Check the following
                                campaigns:
                            </div>

                            <ul>
                                {map(codeAlreadyExistsError.field.codes.codes, (couponName, code) => (
                                    <li key={code} className="no-translate">
                                        <strong>{couponName}</strong> ({code.toUpperCase()})
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Alert>

                    <div className="mt-4 space-y-2">
                        <label className="flex items-center space-x-2">
                            <input
                                checked={duplicateOption === CouponCodeDuplicateOption.SKIP}
                                data-testid="coupon-code-input-without-duplicate-codes"
                                type="radio"
                                onChange={() => {
                                    onDuplicateOptionChange(CouponCodeDuplicateOption.SKIP);
                                }}
                            />

                            <span>Retry upload without duplicate codes</span>
                        </label>

                        <label className="flex items-center space-x-2">
                            <input
                                checked={duplicateOption === CouponCodeDuplicateOption.VOID}
                                type="radio"
                                data-testid="coupon-code-input-void-diplicate-codes"
                                onChange={() => {
                                    onDuplicateOptionChange(CouponCodeDuplicateOption.VOID);
                                }}
                            />

                            <span>Retry upload with all codes and void duplicate codes in other coupon campaigns</span>
                        </label>
                    </div>
                </div>
            ) : null}
        </>
    );
};

interface CodeAlreadyExistsError {
    field: {
        codes: {
            error: "code_already_exists";
            message: string;
            codes: Record<string, string>;
        };
    };
}
